import {
  Scene,
  Vector3,
  AbstractMesh,
  TransformNode,
  Mesh,
  CubeTexture,
} from 'babylonjs';
import type { ContentInstanceManager } from '../../content/ContentInstanceManager';
import type { ContentInstance } from '../../content/types';
import type { PreviewView } from './types';
import type { IAssetModel } from '@webcore/core/loader/loader';

// ============================================================================
// PREVIEW RENDERER
// ============================================================================
// Handles rendering logic for preview canvases.
// Focused single responsibility: rendering only.

/**
 * Renderer for preview canvases
 */
export class PreviewRenderer {
  private static readonly PREVIEW_TARGET_FILL_DESKTOP = 0.7;
  private static readonly PREVIEW_TARGET_FILL_MOBILE = 0.8;
  private static readonly MOBILE_BREAKPOINT_PX = 540;
  private static readonly IMAGE_ENVIRONMENT_LEVEL = 0.6;
  private instanceManager: ContentInstanceManager;
  private views: Map<HTMLCanvasElement, PreviewView>;
  private visibilitySignatures: WeakMap<HTMLCanvasElement, string>;
  private activeSkyboxOwner: PreviewView | null;
  private activeSkyboxRef: Mesh | null;
  private previewPivotCenters: WeakMap<HTMLCanvasElement, Vector3>;

  constructor(
    instanceManager: ContentInstanceManager,
    views: Map<HTMLCanvasElement, PreviewView>
  ) {
    this.instanceManager = instanceManager;
    this.views = views;
    this.visibilitySignatures = new WeakMap();
    this.activeSkyboxOwner = null;
    this.activeSkyboxRef = null;
    this.previewPivotCenters = new WeakMap();
  }

  /**
   * Render a preview for a specific canvas
   */
  renderPreview(canvas: HTMLCanvasElement, scene: Scene): void {
    const preview = this.views.get(canvas);
    if (!preview || !preview.view.enabled) return;

    const instance = this.instanceManager.getInstance(preview.contentId);

    if (!instance || !instance.isLoaded) {
      this.renderEmptyScene(preview, scene);
      return;
    }

    // Frame model if needed
    if (!preview.isFramed || preview.lastFramedContentId !== preview.contentId) {
      this.frameModelForPreview(preview, instance);
      preview.isFramed = true;
      preview.lastFramedContentId = preview.contentId;
      preview.animationsStarted = false;
    }

    // Start animations
    if (!preview.animationsStarted) {
      this.startInstanceAnimations(instance);
      preview.animationsStarted = true;
    }

    // Update camera from gesture controller
    this.updateCameraFromGestures(preview);

    // Setup visibility
    const allInstances = this.instanceManager.getAllInstances();
    const hiddenInstances = this.hideOtherInstances(allInstances, instance);
    if (this.shouldRefreshInstanceVisibility(preview, instance)) {
      this.showInstance(instance, preview.excludeMeshes, preview.includeMeshes);
      this.visibilitySignatures.set(canvas, this.getVisibilitySignature(preview));
    }
    if (this.shouldRefreshSkyboxVisibility(preview)) {
      this.hideOtherSkyboxes(preview);
      this.activeSkyboxOwner = preview;
      this.activeSkyboxRef = preview.skybox;
    }

    // Apply preview-only pivot/rotation around filtered mesh center.
    const originalTransform = this.applyPreviewTransform(instance, preview);

    // Store original scene state
    const originalCamera = scene.activeCamera;
    const originalClearColor = scene.clearColor.clone();
    const originalEnvTexture = scene.environmentTexture;
    const originalPreviewEnvironmentLevel = this.applyPreviewEnvironmentTexture(preview, scene);

    // Apply preview settings
    scene.clearColor = preview.backgroundColor;
    scene.activeCamera = preview.camera;

    if (preview.skybox) {
      preview.skybox.setEnabled(true);
      preview.skybox.isPickable = false;
    }

    // Render
    scene.render();

    // Restore scene state
    scene.activeCamera = originalCamera;
    scene.clearColor = originalClearColor;
    scene.environmentTexture = originalEnvTexture;
    this.restorePreviewEnvironmentTexture(preview, originalPreviewEnvironmentLevel);

    // Disable skybox so it never leaks into main/AR view
    if (preview.skybox) preview.skybox.setEnabled(false);

    // Restore instance visibility
    this.restoreInstances(hiddenInstances);
    this.restorePreviewTransform(originalTransform);

    // Mark as ready on first render
    if (!preview.isReady) {
      preview.isReady = true;
    }
  }

  /**
   * Frame a model to fit the preview camera.
   * Automatically calculates camera distance based on model size, canvas dimensions,
   * and camera FOV so all models appear at consistent size (like model-viewer).
   */
  frameModelForPreview(preview: PreviewView, instance: ContentInstance): void {
    const camera = preview.camera;
    const canvas = preview.canvas;
    const { gestureController } = preview;
    
    // Reset gesture controller
    gestureController.resetVelocity();
    
    if (!instance.rootNode) return;
    
    // Build bounds from strictly matched preview meshes for consistent framing.
    const meshesToInclude = this.getMeshesForPreviewBounds(preview, instance);
    
    if (meshesToInclude.length === 0) return;
    
    // Calculate combined bounding box from ALL meshes
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    
    for (const mesh of meshesToInclude) {
      mesh.computeWorldMatrix(true);
      const bounds = mesh.getBoundingInfo().boundingBox;
      const worldMin = bounds.minimumWorld;
      const worldMax = bounds.maximumWorld;
      
      minX = Math.min(minX, worldMin.x);
      minY = Math.min(minY, worldMin.y);
      minZ = Math.min(minZ, worldMin.z);
      maxX = Math.max(maxX, worldMax.x);
      maxY = Math.max(maxY, worldMax.y);
      maxZ = Math.max(maxZ, worldMax.z);
    }
    
    const boundsSize = new Vector3(maxX - minX, maxY - minY, maxZ - minZ);
    const center = new Vector3((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2);
    this.previewPivotCenters.set(preview.canvas, center.clone());
    
    // Reset camera offsets
    camera.targetScreenOffset.x = 0;
    camera.targetScreenOffset.y = 0;
    
    // Set camera target to model center
    camera.setTarget(center);
    
    // =========================================================================
    // Auto-framing: Calculate camera distance to fit model in canvas
    // =========================================================================
    
    // Get canvas dimensions
    const canvasWidth = canvas.clientWidth || canvas.width;
    const canvasHeight = canvas.clientHeight || canvas.height;
    const aspectRatio = canvasWidth / canvasHeight;
    
    // Camera vertical FOV (Babylon.js default is ~0.8 radians ≈ 45°)
    const vFov = camera.fov || 0.8;
    // Calculate horizontal FOV from aspect ratio
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspectRatio);
    
    // Model dimensions (height and width as seen from front)
    const modelHeight = boundsSize.y;
    const modelWidth = Math.max(boundsSize.x, boundsSize.z);
    const previewTargetFill =
      canvasWidth <= PreviewRenderer.MOBILE_BREAKPOINT_PX
        ? PreviewRenderer.PREVIEW_TARGET_FILL_MOBILE
        : PreviewRenderer.PREVIEW_TARGET_FILL_DESKTOP;

    // Calculate distance needed to fit model in each dimension
    // Formula: distance = (size / 2) / tan(fov / 2)
    const distForHeight = ((modelHeight / 2) / Math.tan(vFov / 2)) / previewTargetFill;
    const distForWidth = ((modelWidth / 2) / Math.tan(hFov / 2)) / previewTargetFill;
    
    // Use whichever requires more distance (so model fits both ways)
    const fitDistance = Math.max(distForHeight, distForWidth);
    
    // Use fixed composition target so all models occupy similar preview area.
    camera.radius = Math.max(fitDistance, 0.01);
    
    // Set camera angles - front view with slight angle
    const baseAlpha = Math.PI / 2 + (25 * Math.PI / 180);
    camera.alpha = baseAlpha;
    camera.beta = Math.PI / 2;
    
    // Set camera clipping planes based on distance
    const radius = camera.radius;
    camera.minZ = Math.max(0.01, radius * 0.01);
    camera.maxZ = Math.max(1000, radius * 10);
    
    // Store base radius
    preview.baseRadius = radius;
    
    // Apply viewer offset (scaled proportionally)
    if (preview.viewerOffset.x !== 0 || preview.viewerOffset.y !== 0) {
      camera.targetScreenOffset.x = -preview.viewerOffset.x * radius;
      camera.targetScreenOffset.y = preview.viewerOffset.y * radius;
    }
    
    // Sync gesture controller with camera
    gestureController.setState({ 
      baseRadius: radius,
      radius: radius,
      alpha: camera.alpha,
      beta: camera.beta,
    });
    
    // Set zoom limits
    camera.lowerRadiusLimit = radius * 0.5;
    camera.upperRadiusLimit = radius * 2;
  }
  
  /**
   * Find the first mesh in a node hierarchy
   */
  private findFirstMesh(node: TransformNode): AbstractMesh | null {
    if ((node as AbstractMesh).getTotalVertices && (node as AbstractMesh).getTotalVertices() > 0) {
      return node as AbstractMesh;
    }
    
    const children = node.getChildMeshes(false);
    for (const child of children) {
      if (child.getTotalVertices && child.getTotalVertices() > 0) {
        return child;
      }
    }
    
    return children.length > 0 ? children[0] : null;
  }

  // ===========================================================================
  // Private helpers
  // ===========================================================================

  private renderEmptyScene(preview: PreviewView, scene: Scene): void {
    const allInstances = this.instanceManager.getAllInstances();

    // Hide all models
    for (const inst of allInstances) {
      if (inst.rootNode) inst.rootNode.setEnabled(false);
    }

    // Show only this preview's skybox
    for (const p of this.views.values()) {
      if (p.skybox) {
        if (p === preview) {
          p.skybox.setEnabled(true);
          p.skybox.isPickable = false;
        } else {
          p.skybox.setEnabled(false);
        }
      }
    }

    const originalCamera = scene.activeCamera;
    const originalClearColor = scene.clearColor.clone();
    const originalEnvTexture = scene.environmentTexture;
    const originalPreviewEnvironmentLevel = this.applyPreviewEnvironmentTexture(preview, scene);

    scene.clearColor = preview.backgroundColor;
    scene.activeCamera = preview.camera;

    scene.render();

    scene.activeCamera = originalCamera;
    scene.clearColor = originalClearColor;
    scene.environmentTexture = originalEnvTexture;
    this.restorePreviewEnvironmentTexture(preview, originalPreviewEnvironmentLevel);
    for (const p of this.views.values()) {
      if (p.skybox) p.skybox.setEnabled(false);
    }
  }

  private applyPreviewEnvironmentTexture(preview: PreviewView, scene: Scene): number | null {
    if (preview.iblEnvironmentTexture && preview.iblEnvironmentTexture.isReady()) {
      scene.environmentTexture = preview.iblEnvironmentTexture;
      return null;
    }

    if (!preview.environmentTexture || !preview.environmentTexture.isReady()) {
      return null;
    }

    const originalLevel = preview.environmentTexture.level;
    const environmentPath = preview.environmentPath?.split('?')[0]?.toLowerCase() ?? '';
    if (environmentPath && !environmentPath.endsWith('.env')) {
      preview.environmentTexture.level = PreviewRenderer.IMAGE_ENVIRONMENT_LEVEL;
    }

    scene.environmentTexture = preview.environmentTexture;
    return originalLevel;
  }

  private restorePreviewEnvironmentTexture(preview: PreviewView, originalLevel: number | null): void {
    if (originalLevel === null || !preview.environmentTexture) return;
    preview.environmentTexture.level = originalLevel;
  }

  private updateCameraFromGestures(preview: PreviewView): void {
    const gc = preview.gestureController;
    const camera = preview.camera;

    // Update gesture controller (processes inertia/velocity)
    gc.update();

    // Apply gesture state directly to camera (state stores absolute values)
    const state = gc.state;
    camera.alpha = state.alpha;
    camera.beta = state.beta;
    camera.radius = state.radius;
  }

  private getMeshesForPreviewBounds(preview: PreviewView, instance: ContentInstance): AbstractMesh[] {
    const excludePatterns = preview.excludeMeshes?.map(p => p.toLowerCase()) ?? [];
    const includePatterns = preview.includeMeshes?.map(p => p.toLowerCase()) ?? [];
    const hasInclude = includePatterns.length > 0;

    const shouldShow = (meshName: string): boolean => {
      const name = meshName.toLowerCase();
      if (hasInclude) {
        return includePatterns.some(p => name.includes(p));
      }
      if (excludePatterns.length > 0) {
        return !excludePatterns.some(p => name.includes(p));
      }
      return true;
    };

    const candidateMeshes: AbstractMesh[] = [];
    for (const asset of instance.assets) {
      if (asset.type !== 'model') continue;
      const modelAsset = asset as IAssetModel;
      if (modelAsset.meshes?.length) {
        candidateMeshes.push(...modelAsset.meshes);
      }
    }

    if (candidateMeshes.length === 0 && instance.rootNode) {
      candidateMeshes.push(...instance.rootNode.getChildMeshes(false));
    }

    const boundedMeshes = new Set<AbstractMesh>();
    for (const mesh of candidateMeshes) {
      if (mesh.getTotalVertices() <= 0) continue;
      if (!shouldShow(mesh.name ?? '')) continue;
      boundedMeshes.add(mesh);
    }

    return Array.from(boundedMeshes);
  }

  private getBoundsCenter(meshes: AbstractMesh[]): Vector3 | null {
    if (meshes.length === 0) return null;

    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

    for (const mesh of meshes) {
      mesh.computeWorldMatrix(true);
      const bounds = mesh.getBoundingInfo().boundingBox;
      const worldMin = bounds.minimumWorld;
      const worldMax = bounds.maximumWorld;

      minX = Math.min(minX, worldMin.x);
      minY = Math.min(minY, worldMin.y);
      minZ = Math.min(minZ, worldMin.z);
      maxX = Math.max(maxX, worldMax.x);
      maxY = Math.max(maxY, worldMax.y);
      maxZ = Math.max(maxZ, worldMax.z);
    }

    return new Vector3((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2);
  }

  private applyPreviewTransform(
    instance: ContentInstance,
    preview: PreviewView
  ): { rootNode: TransformNode; rotation: Vector3; pivotPoint: Vector3 } | null {
    if (!instance.rootNode) return null;

    const rootNode = instance.rootNode;
    const original = {
      rootNode,
      rotation: rootNode.rotation.clone(),
      pivotPoint: rootNode.getPivotPoint().clone(),
    };

    let worldPivot = this.previewPivotCenters.get(preview.canvas);
    if (!worldPivot) {
      const meshes = this.getMeshesForPreviewBounds(preview, instance);
      worldPivot = this.getBoundsCenter(meshes) ?? rootNode.getAbsolutePosition().clone();
      this.previewPivotCenters.set(preview.canvas, worldPivot.clone());
    }

    const inverseWorld = rootNode.getWorldMatrix().clone().invert();
    const localPivot = Vector3.TransformCoordinates(worldPivot, inverseWorld);
    rootNode.setPivotPoint(localPivot);
    rootNode.rotation.y = preview.rotationY * Math.PI / 180;

    return original;
  }

  private restorePreviewTransform(
    transform: { rootNode: TransformNode; rotation: Vector3; pivotPoint: Vector3 } | null
  ): void {
    if (!transform) return;
    transform.rootNode.rotation.copyFrom(transform.rotation);
    transform.rootNode.setPivotPoint(transform.pivotPoint);
  }

  private getVisibilitySignature(preview: PreviewView): string {
    const exclude = (preview.excludeMeshes ?? []).join('|');
    const include = (preview.includeMeshes ?? []).join('|');
    return `${preview.contentId}::${preview.rotationY}::${exclude}::${include}`;
  }

  private shouldRefreshInstanceVisibility(preview: PreviewView, instance: ContentInstance): boolean {
    if (!instance.rootNode || !instance.rootNode.isEnabled()) {
      return true;
    }

    return this.visibilitySignatures.get(preview.canvas) !== this.getVisibilitySignature(preview);
  }

  private shouldRefreshSkyboxVisibility(preview: PreviewView): boolean {
    return this.activeSkyboxOwner !== preview || this.activeSkyboxRef !== preview.skybox;
  }

  private hideOtherInstances(
    allInstances: ContentInstance[],
    current: ContentInstance
  ): { instance: ContentInstance; wasEnabled: boolean }[] {
    const hidden: { instance: ContentInstance; wasEnabled: boolean }[] = [];

    for (const inst of allInstances) {
      if (inst !== current && inst.rootNode) {
        hidden.push({ instance: inst, wasEnabled: inst.rootNode.isEnabled() });
        inst.rootNode.setEnabled(false);
      }
    }

    return hidden;
  }

  private showInstance(
    instance: ContentInstance,
    excludeMeshes: string[] | null,
    includeMeshes: string[] | null
  ): void {
    if (!instance.rootNode) return;

    instance.rootNode.setEnabled(true);

    const excludePatterns = excludeMeshes?.map(p => p.toLowerCase()) ?? [];
    const includePatterns = includeMeshes?.map(p => p.toLowerCase()) ?? [];
    const hasFilters = excludePatterns.length > 0 || includePatterns.length > 0;

    // Helper to check if mesh should be visible
    const shouldShow = (meshName: string): boolean => {
      const name = meshName.toLowerCase();
      if (includePatterns.length > 0) {
        return includePatterns.some(p => name.includes(p));
      }
      if (excludePatterns.length > 0) {
        return !excludePatterns.some(p => name.includes(p));
      }
      return true;
    };

    // Helper to enable all ancestors of a mesh up to the root
    const enableAncestors = (mesh: AbstractMesh): void => {
      let parent = mesh.parent;
      while (parent) {
        // Ancestors must be enabled so included descendants can render,
        // but we do not force ancestor mesh geometry visible.
        parent.setEnabled(true);
        parent = parent.parent;
      }
    };

    // Apply to all model assets
    for (const asset of instance.assets) {
      if (asset.type !== 'model') continue;
      const modelAsset = asset as IAssetModel;

      // Always keep meshRoot enabled so children can render
      if (modelAsset.meshRoot) {
        modelAsset.meshRoot.setEnabled(true);
        if (modelAsset.meshRoot instanceof AbstractMesh) {
          modelAsset.meshRoot.isVisible = true;
          modelAsset.meshRoot.visibility = 1;
        }
      }

      if (modelAsset.meshes) {
        // First pass: determine visibility
        const meshVisibility = new Map<AbstractMesh, boolean>();
        for (const mesh of modelAsset.meshes) {
          if (!hasFilters) {
            meshVisibility.set(mesh, true);
          } else {
            meshVisibility.set(mesh, shouldShow(mesh.name ?? ''));
          }
        }

        // Second pass: apply visibility and enable ancestors for visible meshes
        for (const mesh of modelAsset.meshes) {
          const visible = meshVisibility.get(mesh) ?? false;
          mesh.setEnabled(visible);
          mesh.isVisible = visible;
          mesh.visibility = visible ? 1 : 0;

          // If this mesh is visible and using includeMeshes, enable all ancestors
          if (visible && includePatterns.length > 0) {
            enableAncestors(mesh);
          }
        }
      }
    }
  }

  private restoreInstances(
    hidden: { instance: ContentInstance; wasEnabled: boolean }[]
  ): void {
    for (const { instance, wasEnabled } of hidden) {
      if (instance.rootNode) {
        instance.rootNode.setEnabled(wasEnabled);
      }
    }
  }

  private hideOtherSkyboxes(current: PreviewView): void {
    for (const p of this.views.values()) {
      if (p !== current && p.skybox) {
        p.skybox.setEnabled(false);
      }
    }
  }

  private startInstanceAnimations(instance: ContentInstance): void {
    if (!instance.rootNode) return;

    for (const asset of instance.assets) {
      if (asset.type !== 'model') continue;
      const modelAsset = asset as IAssetModel;
      const anims = modelAsset.animations ?? [];
      for (const group of anims) {
        if (!group.isPlaying) {
          group.start(true);
        }
      }
    }
  }
}
